import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, Lock } from "lucide-react";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { API, money } from "./shared";
import { GrossBidNotice } from "./GrossBid";

// ---------- Phase B · Agency quote form ----------
export function AgencyQuoteForm({ brief, close, refresh }) {
  const [existing, setExisting] = useState(null);
  const [price, setPrice] = useState("");
  const [revRate, setRevRate] = useState("");
  const [days, setDays] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  useEffect(() => {
    axios.get(`${API}/design/briefs/${brief.id}/quotes`).then(r => {
      const mine = (r.data.quotes || [])[0];
      if (mine) {
        setExisting(mine);
        setPrice(String(mine.price_inr));
        setRevRate(mine.revision_rate_inr ? String(mine.revision_rate_inr) : "");
        setDays(mine.delivery_days ? String(mine.delivery_days) : "");
        setNote(mine.note || "");
      }
    }).catch(() => {});
  }, [brief.id]);
  const submit = async (e) => {
    e.preventDefault();
    if (!price || Number(price) <= 0) { setErr("Project price must be > 0"); return; }
    if (!revRate || Number(revRate) <= 0) { setErr("Paid revision rate is required"); return; }
    setBusy(true); setErr(""); setOk("");
    const wasNew = !existing;
    try {
      await axios.post(`${API}/design/briefs/${brief.id}/quotes`, {
        price_inr: Number(price),
        revision_rate_inr: Number(revRate),
        delivery_days: days ? Number(days) : null,
        note: note || null,
      });
      toast.success(wasNew ? "Quote submitted" : "Quote updated", {
        description: `${brief.title} · ₹${Number(price).toLocaleString("en-IN")}${days ? ` · ${days} days` : ""}. You'll be notified if the buyer awards you.`,
      });
      setOk(wasNew ? "Quote submitted." : "Quote updated.");
      refresh();
      if (wasNew && close) close();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit quote");
    } finally { setBusy(false); }
  };
  return (
    <>
      <div className="drawer-section-title" style={{marginTop:20}}><h3>{existing ? "Update your quote" : "Submit a quote"}</h3><span>Reverse-bidding</span></div>
      <form onSubmit={submit} className="studio-form" style={{maxWidth:"none",gridTemplateColumns:"1fr 1fr"}}>
        {brief.budget_inr ? (
          <div className="modal-full-row" data-testid="quote-buyer-budget" style={{fontSize:12,color:"#1E40AF",background:"#F0F6FF",border:"1px solid #BFDBFE",borderRadius:6,padding:"8px 10px"}}>
            Buyer budget: <b>{money(brief.budget_inr)}</b> — match it or propose your own fixed project price. 5 revisions are free; set your rate for each extra round.
            {!price && <button type="button" data-testid="quote-match-budget" onClick={() => setPrice(String(brief.budget_inr))} style={{marginLeft:8,border:0,background:"none",color:"#1d4ed8",textDecoration:"underline",cursor:"pointer",font:"inherit"}}>Match budget</button>}
          </div>
        ) : null}
        <label>Fixed project price (INR, gross — incl. our commission) *
          <input data-testid="quote-price-input" type="number" min="1" value={price} onChange={e => setPrice(e.target.value)} required/>
        </label>
        <GrossBidNotice amount={price} label="quote" gstRate={18}/>
        <label>Paid revision rate (INR / round after 5 free) *
          <input data-testid="quote-revision-rate-input" type="number" min="1" value={revRate} onChange={e => setRevRate(e.target.value)} required placeholder="e.g. 500"/>
        </label>
        <label>Delivery (days)
          <input data-testid="quote-days-input" type="number" min="1" value={days} onChange={e => setDays(e.target.value)} placeholder="e.g. 5"/>
        </label>
        <label style={{gridColumn:"span 2"}}>Note to buyer (optional)
          <textarea data-testid="quote-note-input" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Includes revisions, deliverable formats, timeline…" style={{font:"13px Outfit",padding:10,border:"1px solid var(--line)",borderRadius:5,color:"var(--ink)"}}/>
        </label>
        {err && <div className="modal-full-row" style={{color:"#b91c1c",fontSize:12}} data-testid="quote-err">{err}</div>}
        {ok && <div className="modal-full-row" style={{color:"#059669",fontSize:12}} data-testid="quote-ok">{ok}</div>}
        <div className="form-actions" style={{gridColumn:"span 2"}}>
          <button data-testid="quote-submit-button" type="submit" className="primary-btn" disabled={busy}>
            {busy ? "Sending…" : existing ? "Update quote" : "Send quote"} <Sparkles size={15}/>
          </button>
        </div>
      </form>
    </>
  );
}

// ---------- Phase B · Buyer quotes panel (bidding) ----------
export function BuyerQuotesPanel({ brief, quotes, user, tierLabel, onAwarded, setPayErr }) {
  const [awarding, setAwarding] = useState(null);
  const award = async (quote) => {
    if (!window.confirm(`Award to ${quote.agency_name} at a fixed price of ₹${quote.price_inr.toLocaleString("en-IN")} (5 free revisions, then ₹${Number(quote.revision_rate_inr || 0).toLocaleString("en-IN")}/revision)? Escrow will be locked immediately.`)) return;
    setAwarding(quote.id); setPayErr("");
    try {
      const [cfg, order] = await Promise.all([
        axios.get(`${API}/razorpay/config`).then(r => r.data),
        axios.post(`${API}/design/briefs/${brief.id}/award-quote/${quote.id}`).then(r => r.data),
      ]);
      const resp = await openRazorpayCheckout({
        keyId: cfg.key_id,
        order,
        name: "GoPrintPack · Design escrow",
        description: `Design brief ${brief.id} · ${quote.agency_name}`,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        notes: { brief_id: brief.id, quote_id: quote.id, agency_id: quote.agency_id, flow: "quote_award" },
      });
      await axios.post(`${API}/razorpay/verify`, resp);
      onAwarded();
    } catch (e) {
      if (e?.code === "USER_CANCELLED") setPayErr("Payment cancelled. Award not locked yet.");
      else setPayErr(e?.description || e?.response?.data?.detail || "Escrow payment failed. Try again.");
    } finally { setAwarding(null); }
  };
  return (
    <>
      <div className="drawer-section-title" style={{marginTop:20}}><h3>Agency quotes</h3><span>{quotes.length} submitted</span></div>
      {quotes.length === 0 ? (
        <div className="pipeline-empty" data-testid="brief-no-quotes">No quotes yet. Verified agencies matching your brief will see it in their inbox.</div>
      ) : (
        <div className="quote-list" data-testid="brief-quote-list">
          {quotes.map(q => (
            <div key={q.id} className="quote-card" data-testid={`quote-card-${q.id}`}>
              <div className="quote-card-head">
                <div>
                  <b>{q.agency_name}</b>
                  {q.agency_tier && <span className={`dtier-badge dtier-${q.agency_tier}`} style={{marginLeft:8}}><ShieldCheck size={11}/> {tierLabel(q.agency_tier)}</span>}
                </div>
                <b style={{fontSize:16,color:"var(--blue)"}}>{money(q.price_inr)}</b>
              </div>
              <div className="quote-card-body">
                <div className="spec-chip-row" style={{gap:6}}>
                  <span className="spec-chip" data-testid={`quote-rating-${q.id}`}>★ {q.agency_rating ? Number(q.agency_rating).toFixed(1) : "New"}{q.agency_rating_count ? ` (${q.agency_rating_count})` : ""}</span>
                  <span className="spec-chip" data-testid={`quote-completed-${q.id}`}>{q.agency_completed_projects || 0} projects done</span>
                  <span className="spec-chip" data-testid={`quote-revrate-${q.id}`}>5 free revisions · then {q.revision_rate_inr ? money(q.revision_rate_inr) : "—"}/round</span>
                  {q.delivery_days ? <span className="spec-chip">⏱ {q.delivery_days} days</span> : null}
                  {brief.budget_inr ? <span className="spec-chip" style={{color: q.price_inr <= brief.budget_inr ? "#15803D" : "#B45309"}}>{q.price_inr <= brief.budget_inr ? "Within budget" : `${money(q.price_inr - brief.budget_inr)} over budget`}</span> : null}
                </div>
                {q.note ? <p style={{margin:"6px 0 0",color:"#475569",fontSize:12}}>{q.note}</p> : null}
              </div>
              <div className="quote-card-actions">
                <button
                  data-testid={`quote-award-${q.id}`}
                  className="primary-btn"
                  disabled={awarding !== null}
                  onClick={() => award(q)}
                >
                  {awarding === q.id ? "Opening Razorpay…" : "Award & fund escrow"} <Lock size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---------- Phase B · Buyer fixed-rate hire panel ----------
export function BuyerFixedHirePanel({ brief, ref_, user, onAwarded, setPayErr }) {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [busy, setBusy] = useState(false);
  const tierLabel = (code) => ref_?.tiers?.find(t => t.code === code)?.label || code;
  // Resolve the agency's published rate for this brief's category. If no rate
  // is set, the agency cannot be hired at a fixed rate — buyer must switch to
  // reverse bidding (so the full escrow is always the agency's own quote).
  const rateFor = (a) => {
    const cat = brief.preferred_specialty || brief.category;
    return Number((a.fixed_rates || {})[cat] || 0) > 0 && Number(a.revision_rate_inr || 0) > 0 ? Number(a.fixed_rates[cat]) : null;
  };
  useEffect(() => {
    const params = [];
    if (brief.preferred_specialty) params.push(`specialty=${brief.preferred_specialty}`);
    if (brief.preferred_tier) params.push(`tier=${brief.preferred_tier}`);
    axios.get(`${API}/design/designers${params.length ? "?" + params.join("&") : ""}`)
      .then(r => setAgencies(r.data || []))
      .catch(() => setAgencies([]))
      .finally(() => setLoading(false));
  }, [brief.preferred_specialty, brief.preferred_tier]);
  const hire = async (agency) => {
    const price = rateFor(agency);
    if (!price || price <= 0) {
      setPayErr("This agency hasn't published a project price and revision rate for this category. Please switch to bidding to collect a quote first.");
      return;
    }
    if (!window.confirm(`Hire ${agency.name} at a fixed price of ₹${price.toLocaleString("en-IN")} (5 free revisions, then ₹${Number(agency.revision_rate_inr).toLocaleString("en-IN")}/revision)? The full order value will be locked in escrow immediately.`)) return;
    setBusy(true); setPayErr(""); setSelectedAgency(agency);
    try {
      const [cfg, order] = await Promise.all([
        axios.get(`${API}/razorpay/config`).then(r => r.data),
        axios.post(`${API}/design/briefs/${brief.id}/hire`, {
          agency_id: agency.id,
          price_inr: price,
        }).then(r => r.data),
      ]);
      const resp = await openRazorpayCheckout({
        keyId: cfg.key_id,
        order,
        name: "GoPrintPack · Design escrow",
        description: `Design brief ${brief.id} · ${agency.name}`,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        notes: { brief_id: brief.id, agency_id: agency.id, flow: "fixed_hire" },
      });
      await axios.post(`${API}/razorpay/verify`, resp);
      onAwarded();
    } catch (e) {
      if (e?.code === "USER_CANCELLED") setPayErr("Payment cancelled. Escrow not locked.");
      else setPayErr(e?.description || e?.response?.data?.detail || "Escrow payment failed. Try again.");
    } finally { setBusy(false); setSelectedAgency(null); }
  };
  return (
    <>
      <div className="drawer-section-title" style={{marginTop:20}}><h3>Pick an agency</h3><span>{agencies.length} verified match{agencies.length === 1 ? "" : "es"}</span></div>
      <div className="drawer-callout" style={{background:"#F0F6FF",border:"1px solid #93C5FD",padding:10,borderRadius:8,marginBottom:12,fontSize:12,color:"#1E40AF"}}>
        Full order value is locked in escrow immediately — released only after you approve the final artwork.
      </div>
      {loading ? <div className="pipeline-empty">Loading agencies…</div> : agencies.length === 0 ? (
        <div className="pipeline-empty" data-testid="fixed-no-agencies">No verified agencies match yet. Try broadening your specialty/tier filter or switch to reverse bidding.</div>
      ) : (
        <div className="quote-list" data-testid="fixed-agency-list">
          {agencies.map(a => {
            const price = rateFor(a);
            const cat = brief.preferred_specialty || brief.category;
            const rate = (a.fixed_rates || {})[cat];
            return (
              <div key={a.id} className="quote-card" data-testid={`fixed-agency-${a.id}`}>
                <div className="quote-card-head">
                  <div>
                    <b>{a.name}</b>
                    {a.design_tier && <span className={`dtier-badge dtier-${a.design_tier}`} style={{marginLeft:8}}><ShieldCheck size={11}/> {tierLabel(a.design_tier)}</span>}
                  </div>
                  <b style={{fontSize:14,color:"var(--blue)"}}>{rate ? money(rate) : "No price published"}</b>
                </div>
                <div className="quote-card-body">
                  <div className="spec-chip-row">
                    <span className="spec-chip">★ {a.quality_rating ? Number(a.quality_rating).toFixed(1) : "New"}</span>
                    <span className="spec-chip">5 free revisions · then {a.revision_rate_inr ? money(a.revision_rate_inr) : "—"}/round</span>
                    {(a.design_specialties || []).map(code => <span key={code} className="spec-chip">{ref_?.specialties?.find(s => s.code === code)?.label || code}</span>)}
                  </div>
                </div>
                <div className="quote-card-actions">
                  <button
                    data-testid={`fixed-pick-${a.id}`}
                    className="primary-btn"
                    disabled={!price || busy}
                    title={!price ? "Agency has no published project price + revision rate for this category" : ""}
                    onClick={() => hire(a)}
                  >
                    {busy && selectedAgency?.id === a.id
                      ? "Opening Razorpay…"
                      : price
                        ? <>Hire for {money(price)} <Lock size={14}/></>
                        : "No rate — switch to bidding"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
